import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { AppHeader } from '@/components/layout/AppHeader';

/**
 * Everboarding Module — Route definitions
 *
 * Public-facing onboarding routes only.
 * Admin panel lives in the Ops Dashboard (root app).
 */

// Lazy-loaded pages from monorepo src/
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const PartialOnboarding = lazy(() => import('@/pages/PartialOnboarding'));
const AgentOnboarding = lazy(() => import('@/pages/AgentOnboarding'));
const AgentClientOnboarding = lazy(() => import('@/pages/AgentClientOnboarding'));
const EverboardingTaskPage = lazy(() => import('@/pages/EverboardingTaskPage'));
const CreditAgreementPage = lazy(() => import('@/pages/CreditAgreementPage'));
const ConfirmGdprConsent = lazy(() => import('@/pages/ConfirmGdprConsent'));
const Login = lazy(() => import('./pages/Login'));
const Home = lazy(() => import('./pages/Home'));
const OnboardingPage = lazy(() =>
  import('@/pages/admin/OnboardingPage').then((m) => ({ default: m.OnboardingPage }))
);
const OnboardingFlowDetail = lazy(() =>
  import('@/pages/admin/OnboardingFlowDetail').then((m) => ({ default: m.OnboardingFlowDetail }))
);
const NotFound = lazy(() => import('@/pages/NotFound'));
const Welcome = lazy(() => import('@/pages/Welcome'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const Signup = lazy(() => import('@/pages/Signup'));
const RelationshipAccept = lazy(() => import('@/pages/RelationshipAccept'));
const SigningDelegation = lazy(() => import('@/pages/SigningDelegation'));
const BillingTransfer = lazy(() => import('@/pages/BillingTransfer'));
const Unauthorized = lazy(() => import('@/pages/Unauthorized'));
const ProfileSettings = lazy(() =>
  import('@/pages/ProfileSettings').then((m) => ({ default: m.ProfileSettings }))
);

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
  >
    {children}
  </motion.div>
);

export const EverboardingRoutes: React.FC = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* ── Home (Onboarding Hub for authenticated admin users) ── */}
      <Route path="/home" element={
        <RequireAuth>
          <PageWrapper>
            <div className="min-h-screen bg-muted/30">
              <AppHeader showBackToPortal />
              <main className="w-full px-2 sm:px-4 lg:px-8 xl:px-12 2xl:px-16 py-6 sm:py-10">
                <OnboardingPage />
              </main>
            </div>
          </PageWrapper>
        </RequireAuth>
      } />

      {/* ── Public routes ──────────────────────────────── */}
      <Route path="/welcome" element={<PageWrapper><Welcome /></PageWrapper>} />
      <Route path="/login" element={<PageWrapper><Login /></PageWrapper>} />
      <Route path="/forgot-password" element={<PageWrapper><ForgotPassword /></PageWrapper>} />
      <Route path="/signup" element={<PageWrapper><Signup /></PageWrapper>} />
      <Route path="/relationship/accept" element={<PageWrapper><RelationshipAccept /></PageWrapper>} />
      <Route path="/delegation/signing" element={<PageWrapper><SigningDelegation /></PageWrapper>} />
      <Route path="/billing/transfer" element={<PageWrapper><BillingTransfer /></PageWrapper>} />
      <Route path="/credit-agreement/:token" element={<PageWrapper><CreditAgreementPage /></PageWrapper>} />
      <Route path="/everboarding" element={<PageWrapper><EverboardingTaskPage /></PageWrapper>} />
      <Route path="/confirm-gdpr" element={<PageWrapper><ConfirmGdprConsent /></PageWrapper>} />

      {/* ── Onboarding form routes ─────────────────────── */}
      <Route path="/onboarding" element={<PageWrapper><Onboarding /></PageWrapper>} />
      <Route path="/onboarding/agent" element={<PageWrapper><AgentOnboarding /></PageWrapper>} />
      <Route path="/onboarding/agent-client" element={<PageWrapper><AgentClientOnboarding /></PageWrapper>} />
      <Route path="/onboarding/supplier" element={<PageWrapper><Onboarding /></PageWrapper>} />
      <Route path="/onboarding/partial" element={<PageWrapper><PartialOnboarding /></PageWrapper>} />

      {/* ── Protected routes ───────────────────────────── */}
      <Route path="/profile-settings" element={
        <RequireAuth>
          <PageWrapper><ProfileSettings /></PageWrapper>
        </RequireAuth>
      } />

      {/* ── Admin sub-routes needed by OnboardingPage ── */}
      <Route path="/admin/onboarding/:flowId" element={
        <RequireAuth>
          <PageWrapper>
            <div className="min-h-screen bg-muted/30">
              <AppHeader showBackToPortal />
              <main className="w-full px-2 sm:px-4 lg:px-8 xl:px-12 2xl:px-16 py-6 sm:py-10">
                <OnboardingFlowDetail />
              </main>
            </div>
          </PageWrapper>
        </RequireAuth>
      } />

      {/* ── Redirect remaining admin/portal routes to home ── */}
      <Route path="/admin/*" element={<Navigate to="/home" replace />} />
      <Route path="/portal/*" element={<Navigate to="/home" replace />} />

      {/* ── Unauthorized ───────────────────────────────── */}
      <Route path="/unauthorized" element={<PageWrapper><Unauthorized /></PageWrapper>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* 404 */}
      <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
    </Routes>
  </Suspense>
);
